// Canonical registry for the "Where am I?" overlay's subway map.
// Geometry, palette, and copy carry over from the Claude Design handoff.

import type { CycleSignalsResponse } from "@/hooks/useCycleSignals";
import { SEED_TARGET_LABEL } from "@/lib/conceptGuidance";

export type StationId = "seed" | "trawl" | "score" | "review" | "analyze" | "build";
export type TabId = "Frame" | "Curate" | "Analyze" | "Build";

export type RouteId =
  | "refine"
  | "audit"
  | "surface_gaps"
  | "drill"
  | "evidence_excerpts"
  | "smart_trawl";

export type RouteGroup = "refinement" | "investigation" | "forward";

export interface Station {
  id: StationId;
  tab: TabId;
  /** CSS variable reference, e.g. "var(--chart-2)". */
  color: string;
  label: string;
  sub: string;
  /** SVG x within the 0..900 viewBox. */
  x: number;
  /** Score is the only transit station — smaller, dashed ring, italic label. */
  transit?: boolean;
  /** Chip row in the StationDetailPanel "Tools here" section. */
  features: string[];
  /** Bulleted prose in the StationDetailPanel "From here" callout. */
  fromHere: string[];
}

export interface Route {
  id: RouteId;
  from: StationId;
  to: StationId;
  label: string;
  /** Subway track y-offset from railY (negative = above rail). */
  yOffset: number;
  /** CSS variable reference for the route's stroke color (categorical palette). */
  color: string;
  group: RouteGroup;
  endXOffset?: number;
  labelXOffset?: number;
  labelInside?: boolean;
}

export const STATIONS: readonly Station[] = [
  {
    id: "seed",
    tab: "Frame",
    color: "var(--chart-2)",
    label: "Seed",
    sub: "the question",
    x: 70,
    features: ["Concept description", "Seed paper picker"],
    fromHere: [
      `Add ${SEED_TARGET_LABEL} seed papers, then trawl.`,
      "Refine your research question anytime to recalibrate scores.",
    ],
  },
  {
    id: "trawl",
    tab: "Curate",
    color: "var(--chart-1)",
    label: "Trawl",
    sub: "find papers",
    x: 200,
    features: ["Trawl", "Snowball", "Spot Trawl", "Import .bib"],
    fromHere: [
      "Run a trawl, or snowball from a high-citation paper.",
      "Import papers from Zotero or a BibTeX file.",
      "Scoring fires automatically once papers land.",
    ],
  },
  {
    id: "score",
    tab: "Curate",
    color: "var(--chart-1)",
    label: "Score",
    sub: "auto",
    x: 290,
    transit: true,
    features: ["Thumbs feedback", "Re-score"],
    fromHere: [
      "Wait for scoring to complete.",
      "Calibrate scoring with thumbs as you review.",
      "Refine your concept and re-score.",
    ],
  },
  {
    id: "review",
    tab: "Curate",
    color: "var(--chart-1)",
    label: "Review",
    sub: "read & filter",
    x: 410,
    features: ["Filters", "PDF reader", "Highlights", "Working sets"],
    fromHere: [
      "From the abstract, dismiss, thumb up/down, or override scores to guide PaperChase's model.",
      "Group papers into Working Sets to focus a sub-corpus.",
      "Open a top paper and fetch its full text to read.",
      "Highlight key passages to attach as evidence.",
    ],
  },
  {
    id: "analyze",
    tab: "Analyze",
    color: "var(--chart-4)",
    label: "Analyze",
    sub: "map the field",
    x: 730,
    features: ["Field debate", "Coverage audit", "Comparison", "Ask the corpus", "Landscape", "Argument critique"],
    fromHere: [
      "Build the Field Debate first.",
      "Click a Field Debate camp to drill into its papers.",
      "Run Coverage Audit to find gaps in your corpus.",
      "Ask your corpus targeted questions.",
      "Map your corpus as a 2D landscape.",
    ],
  },
  {
    id: "build",
    tab: "Build",
    color: "var(--chart-5)",
    label: "Build",
    sub: "stake claim",
    x: 830,
    features: ["Evidence map", "Matrix", "Split", "Writing workspace", "Argument critique", "Export"],
    fromHere: [
      "Create your argument's claims and attach evidence from your corpus — or import an existing outline to seed them.",
      "Outline your lit review using claims.",
      "Spot gaps that send you back to Curate for more papers.",
      "See where your argument sits in the field's debate.",
      "Share or export your literature review.",
    ],
  },
] as const;

export const ROUTES: readonly Route[] = [
  // Above rail — refinement (lavender)
  { id: "refine",       from: "score",   to: "seed",    label: "Refine Concept",    yOffset: -55,  color: "var(--primary)",     group: "refinement" },
  { id: "audit",        from: "analyze", to: "score",   label: "Coverage audit",    yOffset: -95,  color: "var(--primary)",     group: "refinement", labelInside: true },
  { id: "surface_gaps", from: "build",   to: "score",   label: "Surface gaps",      yOffset: -135, color: "var(--primary)",     group: "refinement", labelXOffset: 120 },
  // Below rail — investigations (teal) + forward (peach)
  { id: "drill",             from: "analyze", to: "review", label: "Drill into camp",   yOffset: 55,  color: "var(--accent-teal)", group: "investigation", labelInside: true },
  { id: "evidence_excerpts", from: "review",  to: "build",  label: "Evidence excerpts", yOffset: 95,  color: "var(--chart-5)",     group: "forward",       labelXOffset: 120 },
  { id: "smart_trawl",       from: "review",  to: "trawl",  label: "Spot Trawl",       yOffset: 135, color: "var(--accent-teal)", group: "investigation" },
] as const;

/** SVG layout constants from the handoff. */
export const MAP_VIEWBOX = { w: 900, h: 480 } as const;
export const RAIL_Y = 230;
export const RAIL_X_START = 70;
export const RAIL_X_END = 830;
export const SUBWAY_CORNER_RADIUS = 10;

/**
 * Enacted predicates for all 6 cycles. smart_trawl/evidence_excerpts/
 * surface_gaps shipped with §1251; refine/audit/drill gained backend
 * signals in the 2026-06-11 guidance round (refine: re-score after a
 * concept-version edit; audit: coverage_audit usage event; drill:
 * camp_drill engagement event).
 */
export const ENACTED: Partial<Record<RouteId, (s: CycleSignalsResponse) => boolean>> = {
  smart_trawl: (s) => s.has_smart_trawl_papers,
  evidence_excerpts: (s) => s.has_claims_with_evidence,
  surface_gaps: (s) => s.surface_gaps_enacted,
  refine: (s) => s.refine_enacted,
  audit: (s) => s.audit_enacted,
  drill: (s) => s.drill_enacted,
};
